import { execFile } from 'node:child_process';
import { promisify } from 'node:util';
import { writeFileSync, mkdirSync } from 'node:fs';
import { homedir } from 'node:os';
import { join, resolve, sep, parse as parsePath } from 'node:path';
import { forComponent } from '../src/index';
import type { Hooks, ToolUse } from '../src/index';
import type { Checkpoints } from './checkpoints';
import { mutatedPaths } from './checkpoints';
import { sanitizeLabel } from './util';

const log = forComponent('checkpoints');
const exec = promisify(execFile);

/**
 * Durable, CC-parity file checkpoints for the CLI's **disk mode**. Each checkpointed root (the launch
 * cwd, plus any `--add-dir` mounts outside it) gets a *shadow* git repo with its own `GIT_DIR` over the
 * real tree — so a turn's WHOLE-tree state is snapshotted (capturing bash side-effects too, unlike the
 * structured-edit-only in-memory stack) and survives across sessions. The separate git-dir means the
 * user's real `.git` is never touched.
 *
 * One commit per turn per root on a per-session ref (`refs/heads/<sessionId>`), so each conversation has
 * its own restore-point history and the roots stay in lockstep. Rewinding `reset --hard`s every root back
 * and `clean -fd`s untracked-new files (never `-x`, so ignored dirs like node_modules are preserved).
 * Old conversations' refs are pruned past a retention window and objects reclaimed via `gc --auto`.
 */
const DEFAULT_EXCLUDE = ['.agent/', '.git/', 'node_modules/', 'dist/', 'build/', '.next/', 'target/', '.venv/', '__pycache__/', '*.log'];

interface Commit { sha: string; at: number; label: string; files?: number }

/** Roots too broad to snapshot: the user's home dir or a filesystem root. `git add -A` over these walks
 *  an enormous tree (every project/cache) and effectively never returns — the real cause of the launch
 *  hang at "checkpointing initial workspace snapshot…". Disable file checkpoints rather than freeze. */
function isBroadRoot(p: string): boolean {
  const abs = resolve(p);
  return abs === resolve(homedir()) || abs === parsePath(abs).root;
}

/** One shadow git repo over a single real work-tree. Low-level git plumbing; index space is oldest-first. */
class ShadowRepo {
  ready?: boolean; // undefined = unprobed; false = git/this root unusable
  constructor(private workTree: string, private gitDir: string, private exclude: string[], private git: string, private timeoutMs: number) {}

  /** Absolute work-tree root — used to route force-tracked paths to the repo that contains them. */
  get root(): string { return this.workTree; }

  private async run(...args: string[]): Promise<string> {
    // `timeout` + SIGKILL is the escape hatch: on a pathologically large tree `add -A`/`reset` can walk
    // for minutes and wedge the whole turn. Bound every git op so a slow root aborts instead of hanging;
    // execFile rejects with err.killed=true on timeout, which callers surface as "checkpoints disabled".
    const { stdout } = await exec(this.git, ['-c', 'core.hooksPath=/dev/null', ...args], {
      env: { ...process.env, GIT_DIR: this.gitDir, GIT_WORK_TREE: this.workTree },
      maxBuffer: 64 * 1024 * 1024,
      timeout: this.timeoutMs,
      killSignal: 'SIGKILL',
    });
    return stdout;
  }

  async init(): Promise<boolean> {
    if (this.ready !== undefined) return this.ready;
    try {
      await exec(this.git, ['--version']);
      // Probe for a VALID shadow repo — not mere directory existence. A prior crashed/partial init can
      // leave the gitDir present but not a real repo (no HEAD/config), in which case `existsSync` was
      // true → we'd skip `git init` and then ENOENT on info/exclude → falsely report "git unavailable"
      // in a perfectly good git working tree (real session 20260623-102244). Re-init if it's not valid.
      mkdirSync(this.gitDir, { recursive: true });
      const valid = await this.run('rev-parse', '--git-dir').then(() => true, () => false);
      if (!valid) await this.run('init', '-q');
      // (Re)write excludes every init so an existing shadow repo picks up changes to the default list.
      mkdirSync(join(this.gitDir, 'info'), { recursive: true }); // git init creates this, but be defensive
      writeFileSync(join(this.gitDir, 'info', 'exclude'), this.exclude.join('\n') + '\n');
      this.ready = true;
    } catch (e) { log.debug(`git checkpoints unavailable for ${this.workTree}`, e); this.ready = false; }
    return this.ready;
  }

  /** Point HEAD at the session ref without touching the work-tree. */
  async point(ref: string): Promise<void> { await this.run('symbolic-ref', 'HEAD', ref); }

  async commit(label: string, forced: string[] = []): Promise<void> {
    await this.run('add', '-A'); // honors .gitignore (keeps node_modules etc. out)
    // …then force-add the paths the agent itself touched, so its work is snapshotted even when it
    // lives in a .gitignored dir (e.g. .tmp/tasks/). Bounded to agent-edited files — never the tree.
    for (const p of forced) await this.run('add', '-f', '--', p).catch((e) => log.debug(`force-add failed: ${p}`, e));
    await this.run('commit', '--allow-empty', '-q', '-m', label);
  }

  /** Inject the CURRENT (pre-edit) content of `paths` into the turn-open restore point by amending it.
   *  Lets a rewind recover the ORIGINAL of a .gitignored file the agent is about to overwrite — the
   *  turn-boundary `add -A` would never have captured it. Amend (vs a new commit) keeps one restore
   *  point per turn, so the REPL's turn↔frame mapping stays intact. */
  async amendForced(paths: string[]): Promise<void> {
    for (const p of paths) await this.run('add', '-f', '--', p).catch((e) => log.debug(`force-capture failed: ${p}`, e));
    await this.run('commit', '--amend', '--no-edit', '-q', '--allow-empty').catch((e) => log.debug('amend failed', e));
  }

  /** Commits on `ref`, oldest-first (canonical index space). */
  async log(ref: string): Promise<Commit[]> {
    try {
      const out = await this.run('log', '--format=%H%x00%ct%x00%s', ref);
      return out.split('\n').filter(Boolean).map((l) => {
        const [sha, ct, ...rest] = l.split('\x00');
        return { sha, at: Number(ct) * 1000, label: rest.join('\x00') };
      }).reverse();
    } catch { return []; } // unborn ref / no commits
  }

  /** How many tracked files differ between `sha` and the current work-tree (= rewind impact preview). */
  async filesVsWorktree(sha: string): Promise<number> {
    try { return (await this.run('diff', '--name-only', sha)).split('\n').filter(Boolean).length; } catch { return 0; }
  }

  /** Unified diff between `sha` and the current work-tree (for `/diff`). */
  async diffSince(sha: string): Promise<string> {
    try { return await this.run('diff', sha); } catch { return ''; }
  }

  /** Restore the tree to `sha`; returns counts of reverted tracked files + removed untracked-new. */
  async resetTo(sha: string): Promise<{ restored: number; deleted: number }> {
    let restored = 0, deleted = 0;
    try { restored = (await this.run('diff', '--name-status', sha)).split('\n').filter(Boolean).length; } catch (e) { void e; }
    try { deleted = (await this.run('clean', '-nd')).split('\n').filter(Boolean).length; } catch (e) { void e; }
    await this.run('reset', '--hard', '-q', sha);
    await this.run('clean', '-fd'); // NOT -x: keep ignored dirs (node_modules, …)
    return { restored, deleted };
  }

  /** Delete session refs whose tip is older than `cutoffSec` (keep `keepRef`), then reclaim objects. */
  async prune(cutoffSec: number, keepRef: string): Promise<void> {
    try {
      // for-each-ref has no %x00 escape (unlike git log); refnames can't contain spaces, so split on one.
      const out = await this.run('for-each-ref', '--format=%(committerdate:unix) %(refname)', 'refs/heads/');
      for (const l of out.split('\n').filter(Boolean)) {
        const sp = l.indexOf(' ');
        const ts = Number(l.slice(0, sp)), ref = l.slice(sp + 1);
        if (ref !== keepRef && ts < cutoffSec) await this.run('update-ref', '-d', ref).catch(() => {});
      }
      await this.run('gc', '--auto', '-q').catch(() => {});
    } catch (e) { log.debug('checkpoint prune failed', e); }
  }
}

export class GitCheckpoints implements Checkpoints {
  public options: GitCheckpointsOptions;
  private session: string;
  private repos: ShadowRepo[];
  private started = false;
  private snapshotted = false; // false until the first (potentially slow) full-tree commit lands
  private caches: Commit[][] = []; // per-repo, oldest-first; repos stay in lockstep
  private forced = new Set<string>(); // absolute paths the agent touched → force-tracked despite .gitignore

  public constructor(options?: Partial<GitCheckpointsOptions>) {
    this.options = { ...new GitCheckpointsOptions(), ...options };
    this.session = this.options.sessionId;
    this.repos = this.roots().map((r) => new ShadowRepo(r.workTree, r.gitDir, this.options.exclude, this.options.git, this.options.opTimeoutMs));
    this.caches = this.repos.map(() => []);
  }

  private ref(): string { return `refs/heads/${this.session}`; }

  /** cwd + any add-dir mounts that live OUTSIDE cwd (in-cwd mounts are already covered by the cwd root). */
  private roots(): { workTree: string; gitDir: string }[] {
    const cwd = resolve(this.options.workTree);
    const out = [{ workTree: cwd, gitDir: this.options.gitDir }];
    for (const d of this.options.addDirs ?? []) {
      const abs = resolve(d);
      if (abs === cwd || abs.startsWith(cwd + sep)) continue;   // inside cwd → already snapshotted
      if (cwd.startsWith(abs + sep)) continue;                  // ancestor of cwd → avoid nesting + huge snapshot
      out.push({ workTree: abs, gitDir: join(abs, '.agent', 'checkpoints.git') });
    }
    return out;
  }

  /** Init every usable root once, point HEAD at the session ref, and prune stale history. Drops dead roots. */
  private async start(): Promise<boolean> {
    if (this.started) return this.repos.length > 0;
    this.started = true;
    const usable: ShadowRepo[] = [];
    const caches: Commit[][] = [];
    const cutoff = Math.floor(Date.now() / 1000) - this.options.retainDays * 86400;
    let gitFailed = false; // a root that failed for a git reason (vs. skipped as too-broad) → warrants the git-unavailable notice
    for (const r of this.repos) {
      if (isBroadRoot(r.root)) {
        process.stderr.write(`\x1b[2m  checkpoints: '${r.root}' is too broad (home/root) — file rewind disabled here\x1b[0m\n`);
        continue;
      }
      if (!(await r.init())) { gitFailed = true; continue; }
      await r.point(this.ref());
      await r.prune(cutoff, this.ref());
      usable.push(r); caches.push([]);
    }
    if (!usable.length && gitFailed) // git present but every root failed, or git missing (broad-root skips already warned)
      process.stderr.write('\x1b[2m  checkpoints: git unavailable — file rewind disabled (conversation rewind still works)\x1b[0m\n');
    this.repos = usable; this.caches = caches;
    return usable.length > 0;
  }

  use(sessionId: string): void {
    if (sessionId === this.session) return;
    this.session = sessionId;
    if (this.started) for (const r of this.repos) void r.point(this.ref()).catch((e) => log.debug('re-point failed', e));
  }

  async begin(label: string): Promise<void> {
    if (!(await this.start())) return;
    const msg = sanitizeLabel(label, 72) || '(turn)';
    // The first commit hashes the whole tree — warn (once) only if it's actually slow on a big workspace.
    let slow: ReturnType<typeof setTimeout> | undefined;
    if (!this.snapshotted) slow = setTimeout(() => process.stderr.write('\x1b[2m  checkpointing initial workspace snapshot…\x1b[0m\n'), 1500);
    const survivors: ShadowRepo[] = [], survivorCaches: Commit[][] = [];
    for (let i = 0; i < this.repos.length; i++) {
      const forced = [...this.forced].filter((p) => this.repoIndexFor(p) === i); // this repo's touched paths
      try {
        await this.repos[i].commit(msg, forced);
        survivors.push(this.repos[i]); survivorCaches.push(this.caches[i] ?? []);
      } catch (e) {
        // A timeout (killed by SIGKILL) means the tree is too large to snapshot in bounded time — drop the
        // root so we don't re-hang every turn. Other commit failures are transient → keep the root.
        const timedOut = (e as { killed?: boolean })?.killed;
        if (!this.snapshotted && timedOut) {
          process.stderr.write(`\x1b[2m  checkpoints: '${this.repos[i].root}' too large to snapshot in ${Math.round(this.options.opTimeoutMs / 1000)}s — file rewind disabled here\x1b[0m\n`);
        } else {
          log.debug('checkpoint commit failed', e); // never block a turn
          survivors.push(this.repos[i]); survivorCaches.push(this.caches[i] ?? []);
        }
      }
    }
    if (slow) clearTimeout(slow);
    this.repos = survivors; this.caches = survivorCaches;
    this.snapshotted = true;
  }

  /** Index of the repo whose work-tree contains `abs` (longest-prefix wins for nested roots); -1 if none. */
  private repoIndexFor(abs: string): number {
    let best = -1, bestLen = -1;
    this.repos.forEach((r, i) => {
      const wt = r.root.endsWith(sep) ? r.root.slice(0, -1) : r.root;
      if ((abs === wt || abs.startsWith(wt + sep)) && wt.length > bestLen) { best = i; bestLen = wt.length; }
    });
    return best;
  }

  /** Pre-mutation hook: the FIRST time the agent touches a path in a turn, force-capture its current
   *  content into the turn-open restore point — so even a .gitignored or bash-untracked file the agent
   *  overwrites stays recoverable via /rewind. Best-effort: never throws, never blocks a turn. */
  hooks(): Hooks {
    return {
      preToolUse: async (call: ToolUse) => {
        if (!this.snapshotted || !this.repos.length) return; // no restore point to amend into yet
        for (const rel of mutatedPaths(call)) {
          const abs = resolve(this.options.workTree, rel);
          if (this.forced.has(abs)) continue; // only the first touch matters
          this.forced.add(abs);
          const i = this.repoIndexFor(abs);
          if (i >= 0) await this.repos[i].amendForced([abs]).catch((e) => log.debug('amendForced failed', e));
        }
      },
    };
  }

  async refresh(): Promise<void> {
    if (!(await this.start())) { this.caches = []; return; }
    this.caches = await Promise.all(this.repos.map((r) => r.log(this.ref())));
    // Annotate the primary root's rows with rewind impact (files that would change) for the picker.
    const primary = this.caches[0];
    if (primary?.length) await Promise.all(primary.map(async (c) => { c.files = await this.repos[0].filesVsWorktree(c.sha); }));
  }

  /** Newest-first display rows carrying their oldest-first `index` (mirrors CheckpointStack.list()). */
  list(): { index: number; label: string; at: number; files: number }[] {
    return (this.caches[0] ?? []).map((f, i) => ({ index: i, label: f.label, at: f.at, files: f.files ?? 0 })).reverse();
  }

  get size(): number { return this.caches[0]?.length ?? 0; }

  /** Unified diff of everything this session changed: oldest session checkpoint → current tree, across all roots. */
  async diff(): Promise<string> {
    if (!this.caches[0]?.length) await this.refresh();
    const parts: string[] = [];
    for (let i = 0; i < this.repos.length; i++) {
      const base = this.caches[i]?.[0];
      if (base) parts.push(await this.repos[i].diffSince(base.sha));
    }
    return parts.filter(Boolean).join('\n');
  }

  async rewindTo(index: number): Promise<{ restored: number; deleted: number }> {
    if (!this.caches[0]?.length) await this.refresh();
    if (index < 0 || index >= (this.caches[0]?.length ?? 0)) throw new Error('no such checkpoint');
    let restored = 0, deleted = 0;
    for (let i = 0; i < this.repos.length; i++) {
      const c = this.caches[i];
      if (index >= c.length) continue; // a root added later than this point — nothing to undo there
      const r = await this.repos[i].resetTo(c[index].sha);
      restored += r.restored; deleted += r.deleted;
      this.caches[i] = c.slice(0, index + 1); // commits after the target are gone; target is now HEAD
    }
    return { restored, deleted };
  }
}

export class GitCheckpointsOptions {
  /** Real working tree to snapshot (the launch cwd). */
  workTree = process.cwd();
  /** Isolated git dir for the cwd shadow repo (kept out of the user's real .git). */
  gitDir = join(process.cwd(), '.agent', 'checkpoints.git');
  /** Extra mounted dirs (`--add-dir`); those outside cwd each get their own shadow repo. */
  addDirs: string[] = [];
  /** Conversation id → per-session restore-point ref. */
  sessionId = 'default';
  /** Drop restore-point history for conversations untouched for this many days (then `gc --auto`). */
  retainDays = 30;
  /** Paths the shadow repos always ignore (heavy/derived/VCS), beyond the user's own .gitignore. */
  exclude: string[] = [...DEFAULT_EXCLUDE];
  /** Per-git-op wall-clock cap (ms). A root that can't snapshot within this is dropped, not left to hang. */
  opTimeoutMs = 30000;
  /** git executable. */
  git = 'git';
}
